// Order matters: the generated tokens first (T-029: imported exactly once, before any other CSS), then the
// base stylesheet, then the app. `tokens.css` is a byte-for-byte copy of docs/design/tokens.css — never
// edit it; `verify` step "tokens" fails on drift.
import './styles/tokens.css';
import './styles/base.css';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { fixedClock, systemClock } from '@sl/core';
import { App } from './app/App';
import { createAppApiClient } from './app/api/ApiClientProvider';
import { readAppConfig } from './app/config';
import { readStoredDataset } from './app/dataset';
import { readStoredServer } from './app/server';
import { createQueryClient } from './app/query/queryClient';
import { createAppRouter } from './app/router';
import { installStaleBuildReload } from './app/staleBuild';
import { installTheme } from './app/theme/theme';
import { warmStart } from './app/warmStart';

// BUG-054: before the router mounts, so a chunk that went missing under an open tab (a deploy, a preview
// folder swapped) is answered by ONE silent reload rather than by every route landing in the error boundary.
// A second failure on the same build falls through to the boundary, which says so in words.
installStaleBuildReload({ moduleUrl: import.meta.url });

// Theme: index.html already set `data-theme` before first paint; this re-applies it (idempotent) and starts
// following OS changes (ARCH §9.5).
const theme = installTheme();

// T-283: the address decides which account this load is about, and when it says nothing the choice the account
// switch remembered does — so a reload, and tomorrow morning, stay on the account you picked.
// T-210: and on a build that HAS a backend, which server answers — the backend, or the in-memory demo "Try
// the demo" chose (ARCH §1.2). A demo build consults neither value; see `readAppConfig`.
const config = readAppConfig(import.meta.env, window.location, {
  storedDataset: readStoredDataset(),
  storedServer: readStoredServer(),
});
// D-020: the one system clock, or the QA-pinned one from `?demoNow=`.
const clock = config.demoNow === null ? systemClock : fixedClock(config.demoNow);
// Demo by default: no backend, no network (REQ-001 AC1). The promise resolves when the demo chunk has
// loaded; only routes that call `useApiClient()` wait for it.
const client = createAppApiClient(config, clock);
const queryClient = createQueryClient();
// T-321 / BUG-155 — the shell's two unconditional reads start the moment the client exists rather than when
// React reaches them, so the mount, the session and the saved preferences stop being three serial steps.
// Same factories, same keys, nothing new asked for, and nothing at all on a route with no shell.
void warmStart(queryClient, client, window.location);

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root element');

createRoot(root).render(
  <StrictMode>
    <App
      clock={clock}
      theme={theme}
      client={client}
      config={config}
      queryClient={queryClient}
      createRouter={createAppRouter}
    />
  </StrictMode>,
);
